import React, { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import LoadingSpinner from '../LoadingSpinner'
import { bookingsService } from '../../services/bookings.service'
import { useAuth } from '../../hooks/useAuth'
import { formatDateTime, getStatusColor } from '../../utils/helpers'

interface Booking {
  id: string
  caseId: string
  providerId: string
  serviceType: string
  scheduledDateTime: string
  status: string
  location?: string
  specialRequests?: string
  pricing?: any
  notes?: string
  confirmedAt?: string
  createdAt: string
  // New ambulance-specific fields
  tripType?: string
  accessNotes?: string
  onSiteContact?: {
    name: string
    phone: string
    shareWithProvider: boolean
  }
  ambulanceType?: string
  originAddress?: string
  destinationAddress?: string
  funeralCase: {
    id: string
    caseNumber: string
    deceasedName: string
    status: string
    urgencyLevel?: string
    users: {
      id: string
      firstName: string
      lastName: string
      phone: string
      email: string
    }
  }
  serviceProvider: {
    id: string
    companyName?: string
    serviceType: string
    users: {
      id: string
      firstName: string
      lastName: string
      phone: string
      email: string
    }
  }
}

interface BookingsResponse {
  data: Booking[]
  total: number
  page: number
  limit: number
  totalPages: number
}

interface BookingApprovalProps {
  onActionsComplete?: () => void
}

const BookingApproval: React.FC<BookingApprovalProps> = ({ onActionsComplete }) => {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState<string | null>(null)
  const [processingBulk, setProcessingBulk] = useState<string | null>(null) // For case ID
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalBookings, setTotalBookings] = useState(0)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [approveNotes, setApproveNotes] = useState('')
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false)
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false)
  const [selectedCase, setSelectedCase] = useState<string | null>(null) // Case ID for bulk actions
  const [bulkApproveNotes, setBulkApproveNotes] = useState('')
  const [bulkRejectReason, setBulkRejectReason] = useState('')

  const { user } = useAuth()

  const fetchPendingBookings = async (page = 1) => {
    try {
      setLoading(true)
      setError(null)
      const response: BookingsResponse = await bookingsService.getPendingBookings(page, 10)
      setBookings(response.data)
      setTotalPages(response.totalPages)
      setTotalBookings(response.total)
      setCurrentPage(response.page)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pending bookings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPendingBookings()
  }, [])

  const handleApprove = async (booking: Booking) => {
    setSelectedBooking(booking)
    setApproveNotes('')
    setShowApproveModal(true)
  }

  const confirmApprove = async () => {
    if (!selectedBooking) return

    try {
      setProcessing(selectedBooking.id)
      await bookingsService.approveBooking(selectedBooking.id, approveNotes)
      setShowApproveModal(false)
      setSelectedBooking(null)
      setApproveNotes('')
      // Refresh the bookings list
      await fetchPendingBookings(currentPage)
      // Notify parent component that actions are complete
      onActionsComplete?.()
    } catch (err: any) {
      setError(err.message || 'Failed to approve booking')
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async (booking: Booking) => {
    setSelectedBooking(booking)
    setRejectReason('')
    setShowRejectModal(true)
  }

  const confirmReject = async () => {
    if (!selectedBooking || !rejectReason.trim()) return

    try {
      setProcessing(selectedBooking.id)
      await bookingsService.rejectBooking(selectedBooking.id, rejectReason.trim())
      setShowRejectModal(false)
      setSelectedBooking(null)
      setRejectReason('')
      // Refresh the bookings list
      await fetchPendingBookings(currentPage)
      // Notify parent component that actions are complete
      onActionsComplete?.()
    } catch (err: any) {
      setError(err.message || 'Failed to reject booking')
    } finally {
      setProcessing(null)
    }
  }

  // Bulk action functions
  const handleBulkApprove = (caseId: string) => {
    setSelectedCase(caseId)
    setBulkApproveNotes('')
    setShowBulkApproveModal(true)
  }

  const confirmBulkApprove = async () => {
    if (!selectedCase) return

    const caseBookings = bookings.filter(booking => booking.caseId === selectedCase)

    try {
      setProcessingBulk(selectedCase)

      // Approve all bookings for this case
      const approvePromises = caseBookings.map(booking =>
        bookingsService.approveBooking(booking.id, bulkApproveNotes)
      )

      await Promise.all(approvePromises)

      setShowBulkApproveModal(false)
      setSelectedCase(null)
      setBulkApproveNotes('')
      // Refresh the bookings list
      await fetchPendingBookings(currentPage)
      // Notify parent component that actions are complete
      onActionsComplete?.()
    } catch (err: any) {
      setError(err.message || 'Failed to approve bookings')
    } finally {
      setProcessingBulk(null)
    }
  }

  const handleBulkReject = (caseId: string) => {
    setSelectedCase(caseId)
    setBulkRejectReason('')
    setShowBulkRejectModal(true)
  }

  const confirmBulkReject = async () => {
    if (!selectedCase || !bulkRejectReason.trim()) return

    const caseBookings = bookings.filter(booking => booking.caseId === selectedCase)

    try {
      setProcessingBulk(selectedCase)

      // Reject all bookings for this case
      const rejectPromises = caseBookings.map(booking =>
        bookingsService.rejectBooking(booking.id, bulkRejectReason.trim())
      )

      await Promise.all(rejectPromises)

      setShowBulkRejectModal(false)
      setSelectedCase(null)
      setBulkRejectReason('')
      // Refresh the bookings list
      await fetchPendingBookings(currentPage)
      // Notify parent component that actions are complete
      onActionsComplete?.()
    } catch (err: any) {
      setError(err.message || 'Failed to reject bookings')
    } finally {
      setProcessingBulk(null)
    }
  }

  // Group bookings by case
  const groupBookingsByCase = () => {
    const grouped: { [caseId: string]: Booking[] } = {}

    bookings.forEach(booking => {
      if (!grouped[booking.caseId]) {
        grouped[booking.caseId] = []
      }
      grouped[booking.caseId].push(booking)
    })

    return grouped
  }

  const getServiceIcon = (serviceType: string) => {
    switch (serviceType) {
      case 'AMBULANCE_TRANSPORT': return '🚑'
      case 'GHUSL_SERVICE': return 'GH'
      case 'KAFAN_SERVICE': return 'KF'
      case 'JANAZAH_LEAD': return 'JZ'
      case 'BURIAL_PLOT': return 'BP'
      case 'CATERING': return 'CT'
      case 'FLOWERS': return 'FL'
      case 'TENT_RENTAL': return 'TR'
      case 'PHOTOGRAPHY': return 'PH'
      case 'YASIN_BOOKS': return 'YS'
      case 'ISLAMIC_SOUVENIRS': return 'IS'
      default: return 'SV'
    }
  }

  const getServiceName = (serviceType: string) => {
    switch (serviceType) {
      case 'AMBULANCE_TRANSPORT': return 'Ambulance Transport'
      case 'GHUSL_SERVICE': return 'Ghusl Service'
      case 'KAFAN_SERVICE': return 'Kafan Service'
      case 'JANAZAH_LEAD': return 'Janazah Lead'
      case 'BURIAL_PLOT': return 'Burial Plot'
      case 'CATERING': return 'Catering'
      case 'FLOWERS': return 'Flowers'
      case 'TENT_RENTAL': return 'Tent Rental'
      case 'PHOTOGRAPHY': return 'Photography'
      case 'YASIN_BOOKS': return 'Yasin Books'
      case 'ISLAMIC_SOUVENIRS': return 'Islamic Souvenirs'
      default: return serviceType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  const getUrgencyColor = (urgency?: string) => {
    switch (urgency) {
      case 'IMMEDIATE': return 'bg-red-100 text-red-800'
      case 'TODAY': return 'bg-orange-100 text-orange-800'
      case 'TOMORROW': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Helper functions for ambulance-specific details
  const getTripTypeBadge = (tripType?: string) => {
    if (!tripType) return null;

    switch (tripType) {
      case 'PRIORITY':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          🚑 Priority
        </span>;
      case 'REGULAR':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Regular
        </span>;
      default:
        return null;
    }
  };

  const getAmbulanceTypeLabel = (ambulanceType?: string) => {
    if (!ambulanceType) return 'Basic';

    switch (ambulanceType) {
      case 'BASIC': return 'Basic Ambulance';
      case 'BLS': return 'Basic Life Support';
      case 'ALS': return 'Advanced Life Support';
      case 'NEONATAL': return 'Neonatal Transport';
      default: return ambulanceType.replace(/_/g, ' ');
    }
  };

  const getAmbulanceTypeBadge = (ambulanceType?: string) => {
    const labels: { [key: string]: { color: string, label: string } } = {
      'BASIC': { color: 'bg-gray-100 text-gray-800', label: 'Basic' },
      'BLS': { color: 'bg-green-100 text-green-800', label: 'BLS' },
      'ALS': { color: 'bg-purple-100 text-purple-800', label: 'ALS' },
      'NEONATAL': { color: 'bg-pink-100 text-pink-800', label: 'Neonatal' },
    };

    if (!ambulanceType || !labels[ambulanceType]) return null;

    const { color, label } = labels[ambulanceType];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-neutral-900">Booking Approvals</h1>
            {totalBookings > 0 && (
              <div className="flex items-center space-x-2">
                <Badge className="bg-red-100 text-red-800 border-red-200 px-3 py-1">
                  {totalBookings} booking{totalBookings !== 1 ? 's' : ''}
                </Badge>
                <Badge className="bg-orange-100 text-orange-800 border-orange-200 px-3 py-1">
                  {Object.keys(groupBookingsByCase()).length} case{Object.keys(groupBookingsByCase()).length !== 1 ? 's' : ''}
                </Badge>
              </div>
            )}
          </div>
          <p className="text-neutral-600 mt-1">
            Manage pending booking requests from service providers
          </p>
          {totalBookings > 0 && (
            <p className="text-sm text-neutral-500 mt-1">
              {Object.keys(groupBookingsByCase()).length} case container{Object.keys(groupBookingsByCase()).length !== 1 ? 's' : ''} with {totalBookings} individual booking{totalBookings !== 1 ? 's' : ''} pending approval
            </p>
          )}
        </div>
        {totalBookings === 0 && !loading && (
          <div className="bg-green-50 px-4 py-2 rounded-lg border border-green-200">
            <span className="text-green-800 font-medium flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              All caught up!
            </span>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setError(null)}
                className="mt-2"
              >
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bookings List */}
      {bookings.length === 0 && !loading ? (
        <Card>
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-neutral-900 mb-1">No Pending Bookings</h3>
            <p className="text-neutral-600">All booking requests have been processed</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupBookingsByCase()).map(([caseId, caseBookings]) => (
            <Card key={caseId} className="overflow-hidden border-2 border-neutral-200">
              {/* Unified Case Header - Master Section */}
              <div className="bg-gradient-to-r from-neutral-50 to-neutral-100 border-b border-neutral-200">
                <div className="p-6">
                  {/* Case Title and Client Name */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-2xl text-neutral-900 mb-1">
                        Case #{caseBookings[0]?.funeralCase?.caseNumber || 'N/A'}
                      </h3>
                      <p className="text-xl font-medium text-neutral-700 uppercase tracking-wide">
                        {caseBookings[0]?.funeralCase?.deceasedName || 'Unknown'}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-sm px-3 py-1 font-semibold">
                        {caseBookings.length} PENDING BOOKING{caseBookings.length !== 1 ? 'S' : ''}
                      </Badge>
                      {caseBookings[0]?.funeralCase?.urgencyLevel && (
                        <Badge className={`${getUrgencyColor(caseBookings[0]?.funeralCase?.urgencyLevel)} mt-2`}>
                          {caseBookings[0]?.funeralCase?.urgencyLevel}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Contact and Creation Info - Compact Block */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 mb-6">
                    <div className="flex items-center">
                      <span className="font-medium">Contact:</span>
                      <span className="ml-2">
                        {(() => {
                          const userData = caseBookings[0]?.funeralCase?.user;
                          const email = userData?.email || 'No email';
                          return email;
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium">Status:</span>
                      <span className="ml-2">{caseBookings[0]?.funeralCase?.status || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium">Created:</span>
                      <span className="ml-2">{formatDateTime(caseBookings[0].createdAt)}</span>
                    </div>
                  </div>

                  {/* Top-Level Actions - Prominent */}
                  <div className="flex justify-end">
                    <div className="flex space-x-3">
                      <Button
                        variant="outline"
                        onClick={() => handleBulkReject(caseId)}
                        disabled={processingBulk === caseId}
                        className="min-w-32 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        {processingBulk === caseId ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          'Reject All'
                        )}
                      </Button>
                      <Button
                        onClick={() => handleBulkApprove(caseId)}
                        disabled={processingBulk === caseId}
                        className="min-w-32 bg-green-600 hover:bg-green-700 text-white"
                      >
                        {processingBulk === caseId ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          'Approve All'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actionable Services List - Detail Section */}
              <div className="divide-y divide-neutral-100">
                {caseBookings.map((booking) => (
                  <div key={booking.id} className="p-4 hover:bg-neutral-50 transition-colors">
                    <div className="flex items-center justify-between">
                      {/* Service Information */}
                      <div className="flex items-center space-x-4 flex-1">
                        {/* Service Icon */}
                        <div className="w-12 h-12 bg-neutral-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-neutral-600">
                            {getServiceIcon(booking.serviceType)}
                          </span>
                        </div>

                        {/* Service Details */}
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="font-bold text-neutral-900">
                              {getServiceName(booking.serviceType)}
                            </h4>
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>

                            {/* Show trip type and ambulance type for ambulance bookings */}
                            {booking.serviceType === 'AMBULANCE_TRANSPORT' && (
                              <>
                                {getTripTypeBadge(booking.tripType)}
                                {getAmbulanceTypeBadge(booking.ambulanceType)}
                              </>
                            )}
                          </div>

                          <p className="text-sm text-neutral-600 mt-1">
                            Provider: {booking.serviceProvider?.companyName || `${booking.serviceProvider?.user?.firstName || ''} ${booking.serviceProvider?.user?.lastName || ''}`.trim() || 'Service Provider'}
                          </p>

                          {/* Show pickup and destination for ambulance bookings */}
                          {booking.serviceType === 'AMBULANCE_TRANSPORT' && (
                            <div className="mt-2 space-y-1">
                              {booking.originAddress && (
                                <p className="text-sm text-neutral-600 flex items-center">
                                  <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span className="font-medium">Pickup:</span> {booking.originAddress}
                                </p>
                              )}
                              {booking.destinationAddress && (
                                <p className="text-sm text-neutral-600 flex items-center">
                                  <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                  </svg>
                                  <span className="font-medium">Destination:</span> {booking.destinationAddress}
                                </p>
                              )}
                              {booking.accessNotes && (
                                <p className="text-sm text-amber-600 flex items-center">
                                  <svg className="w-4 h-4 mr-2 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                  </svg>
                                  <span className="font-medium">Access:</span> {booking.accessNotes}
                                </p>
                              )}
                              {booking.onSiteContact && (
                                <p className="text-sm text-blue-600 flex items-center">
                                  <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                  <span className="font-medium">Contact:</span> {booking.onSiteContact.name}
                                  {booking.onSiteContact.shareWithProvider && (
                                    <span className="text-xs ml-2 text-green-600">(Shared with provider)</span>
                                  )}
                                </p>
                              )}
                            </div>
                          )}

                          {booking.specialRequests && (
                            <p className="text-xs text-neutral-500 mt-2 italic">
                              Special Requests: {booking.specialRequests}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Time and Actions */}
                      <div className="flex items-center space-x-4">
                        {/* Time and Date */}
                        <div className="text-right text-sm text-neutral-600">
                          {formatDateTime(booking.scheduledDateTime)}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(booking)}
                            disabled={processing === booking.id || processingBulk === caseId}
                            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            {processing === booking.id ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              'Reject'
                            )}
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleApprove(booking)}
                            disabled={processing === booking.id || processingBulk === caseId}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            {processing === booking.id ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              'Approve'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-neutral-600">
            Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalBookings)} of {totalBookings} bookings
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => fetchPendingBookings(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              Previous
            </Button>
            <span className="text-sm text-neutral-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => fetchPendingBookings(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Approve Booking</h3>
            <p className="text-sm text-neutral-600 mb-4">
              Are you sure you want to approve this booking request?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Approval Notes (Optional)
              </label>
              <textarea
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={3}
                placeholder="Add any notes for the approval..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowApproveModal(false)}
                disabled={processing === selectedBooking.id}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmApprove}
                disabled={processing === selectedBooking.id}
              >
                {processing === selectedBooking.id ? <LoadingSpinner size="sm" /> : 'Approve'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Reject Booking</h3>
            <p className="text-sm text-neutral-600 mb-4">
              Please provide a reason for rejecting this booking request.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Rejection Reason *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={3}
                placeholder="Enter the reason for rejection..."
                required
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                disabled={processing === selectedBooking.id}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmReject}
                disabled={processing === selectedBooking.id || !rejectReason.trim()}
              >
                {processing === selectedBooking.id ? <LoadingSpinner size="sm" /> : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Approve Modal */}
      {showBulkApproveModal && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Bulk Approve All Bookings</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to approve all {groupBookingsByCase()[selectedCase]?.length || 0} bookings for case #{groupBookingsByCase()[selectedCase]?.[0]?.case?.caseNumber || selectedCase}?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                value={bulkApproveNotes}
                onChange={(e) => setBulkApproveNotes(e.target.value)}
                placeholder="Add any notes for this bulk approval..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkApproveModal(false)
                  setSelectedCase(null)
                  setBulkApproveNotes('')
                }}
                disabled={processingBulk === selectedCase}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmBulkApprove}
                disabled={processingBulk === selectedCase}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {processingBulk === selectedCase ? <LoadingSpinner size="sm" /> : 'Approve All'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reject Modal */}
      {showBulkRejectModal && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Bulk Reject All Bookings</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to reject all {groupBookingsByCase()[selectedCase]?.length || 0} bookings for case #{groupBookingsByCase()[selectedCase]?.[0]?.case?.caseNumber || selectedCase}?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={bulkRejectReason}
                onChange={(e) => setBulkRejectReason(e.target.value)}
                placeholder="Provide the reason for rejecting all these bookings..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={3}
                required
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkRejectModal(false)
                  setSelectedCase(null)
                  setBulkRejectReason('')
                }}
                disabled={processingBulk === selectedCase}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmBulkReject}
                disabled={processingBulk === selectedCase || !bulkRejectReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {processingBulk === selectedCase ? <LoadingSpinner size="sm" /> : 'Reject All'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BookingApproval